import React, { useState, useEffect } from 'react';
import { AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { RxCross2 } from 'react-icons/rx';

const OrderRatingReviewSlider: React.FC<any> = ({
  productsList,
  isSliderOpen,
  setIsSliderOpen,
  reviews,
  setReviews,
  handleSubmitRatingReview,
  SERVER_URL,
  Namsteji_Manikchand_Icon,
}) => {
  useEffect(() => {
    if (isSliderOpen) {
      const initializedReviews = productsList.map((product: any) => {
        const disabled = product.user_rating !== null && product.user_rating > 0;
        return {
          item_code: product.item_code,
          rating: disabled ? (product.user_rating ? Number(product.user_rating) : 0) : 0,
          review: disabled ? product.comment || '' : '',
          disabled,
        };
      });
      setReviews(initializedReviews);
    }
  }, [isSliderOpen, productsList, setReviews]);

  /**  Disable Submit Button */
  const isSubmitDisabled = !reviews.some((r: any) => !r.disabled && r.rating > 0);

  const handleRating = (index: number, star: number) => {
    if (reviews[index].disabled) return;
    const normalizedRating = star / 5;

    setReviews((prev: any) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], rating: normalizedRating };
      return updated;
    });
  };

  const handleReviewChange = (index: number, value: string) => {
    if (reviews[index].disabled) return;
    setReviews((prev: any) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], review: value };
      return updated;
    });
  };

  /** Final Submit */
  const handleFinalSubmit = () => {
    const filledReviews = reviews
      .map((r: any, idx: number) => {
        const item = productsList[idx];
        if (!item) return null;

        if (r.disabled) return null;
        if (r.rating === 0 && (!r.review || r.review.trim() === '')) return null;

        return {
          web_item: item.website_name,
          // rating: r.rating.toString(),
          rating: (r.rating * 5).toString(),
          comment: r.review || '',
        };
      })
      .filter(Boolean);
    handleSubmitRatingReview?.(filledReviews);
    setIsSliderOpen(false);
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity duration-300 ease-in-out ${
          isSliderOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsSliderOpen(false)}
      ></div>
      {/* Slider Panel */}
      <div
        className={`fixed top-0 right-0 w-full sm:w-full md:w-full lg:w-[450px] h-full bg-white z-50 shadow-lg overflow-y-auto
          transform transition-transform duration-500 ease-in-out
          ${isSliderOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className='flex justify-between items-center px-6  pt-3'>
          <h2 className='text-lg font-semibold'>How was your order?</h2>
          <button onClick={() => setIsSliderOpen(false)} className='text-2xl text-[#4A2D1A]'>
            <RxCross2 />{' '}
          </button>
        </div>

        {reviews.length === 0 && <p className='text-gray-500 text-sm text-center mt-10'>No products to rate.</p>}

        {reviews.map((reviewItem: any, index: any) => {
          const finalRating = Math.round(reviewItem.rating * 10) / 2;
          const product = productsList[index];
          if (!product) return null;

          const isDisabled = reviewItem.disabled;

          return (
            <div key={product.web_item_name} className='border-b px-6 py-3'>
              {/* Product header */}
              <div className='flex items-center gap-3 mb-2'>
                <img
                  src={
                    product.website_image
                      ? `${SERVER_URL}${product.website_image}`
                      : `${SERVER_URL}${Namsteji_Manikchand_Icon}`
                  }
                  alt={product.web_item_name}
                  className='w-16 h-16 object-cover rounded-md'
                />
                <div>
                  <p className='font-medium text-sm'>{product.web_item_name}</p>
                  {/* Rating stars */}
                  <div className='flex items-center'>
                    <>
                      {[1, 2, 3, 4, 5].map(star => {
                        const isFull = finalRating >= star;
                        const isHalf = finalRating + 0.5 === star;

                        return (
                          <span key={star} className='relative w-5 h-5 inline-block'>
                            {/*  empty star */}
                            <AiOutlineStar
                              size={22}
                              color='gold'
                              className='cursor-pointer absolute top-0 left-0'
                              onClick={() => !isDisabled && handleRating(index, star)}
                            />

                            {/* If FULL star */}
                            {isFull && (
                              <AiFillStar
                                size={22}
                                color='gold'
                                className='absolute top-0 left-0'
                                onClick={() => !isDisabled && handleRating(index, star)}
                              />
                            )}

                            {/*   If HALF star  */}
                            {isHalf && (
                              <AiFillStar
                                size={22}
                                color='gold'
                                className='absolute top-0 left-0 overflow-hidden'
                                style={{ clipPath: 'inset(0 50% 0 0)' }}
                                onClick={() => !isDisabled && handleRating(index, star)}
                              />
                            )}
                          </span>
                        );
                      })}
                    </>
                  </div>
                </div>
              </div>

              <textarea
                rows={3}
                placeholder='Rate your delivery and product experience. Your feedback helps us improve!'
                value={reviewItem.review}
                disabled={isDisabled}
                onChange={e => !isDisabled && handleReviewChange(index, e.target.value)}
                className={`field-sizing-content w-full border rounded-md p-2 text-[12px] focus:outline-none resize-none ${
                  isDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                }`}
              />
            </div>
          );
        })}

        {/* Submit Button */}
        {reviews.length > 0 && (
          <div className='px-2 sm:px-2 md:px-4 lg:px-6 flex justify-center'>
            <button
              onClick={handleFinalSubmit}
              disabled={isSubmitDisabled}
              className={`w-[390px] sm:w-[390px] md:w-[390px] lg:w-[410px] py-2 absolute  bottom-4 rounded-md text-white ${
                isSubmitDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-800 hover:bg-red-700'
              }`}
            >
              Submit
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default OrderRatingReviewSlider;
